import { useCallback, useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';

import { useErrorHandling } from '@app/hooks/useErrorHandling';
import { usePrevious } from '@app/hooks/usePrevious';

import { GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { ACTION_MODE } from '../../ListTaxonomies';
import { TaxonomyTree } from '../TaxonomyTree';
import './styles.css';

interface TaxonomyNodeProps {
  taxonomy: GenericObject;
  actionMode: string | null;
  shouldExpandAll: boolean | null;
  deletedParentAndChildId: GenericObject | null;
  selectedTaxonomy: GenericObject | null;
  handleChangeShouldExpandAll(value: boolean | null): void;
  handleShowInfo(item: GenericObject | null): void;
}

export const TaxonomyNode = ({
  taxonomy,
  actionMode,
  handleChangeShouldExpandAll,
  deletedParentAndChildId,
  handleShowInfo,
  shouldExpandAll,
  selectedTaxonomy,
}: TaxonomyNodeProps) => {
  const dataProvider = useDataProvider();
  const [handleError] = useErrorHandling();
  const [shouldFilterDeletedTaxonomy, setShouldFilterDeletedTaxonomy] = useState(false);
  const [taxonomies, setTaxonomies] = useState([]);

  const prevState = usePrevious({ actionMode });

  const fetchTaxonomies = useCallback(async () => {
    try {
      const { data } = await dataProvider.fetchSubTaxonomies('taxonomies', taxonomy.id);
      setTaxonomies(
        data.sort((a: GenericObject, b: GenericObject) => {
          return (a.position as number) - (b.position as number);
        })
      );
    } catch (error: any) {
      handleError(error);
    }
  }, [dataProvider, taxonomy?.id, handleError]);

  //Fetch taxonomies on mount
  useEffect(() => {
    fetchTaxonomies();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //Remove deleted taxonomy from list
  useEffect(() => {
    if (shouldFilterDeletedTaxonomy) {
      setTaxonomies(taxonomies.filter((item: GenericObject) => item.id !== deletedParentAndChildId?.child_id));
      setShouldFilterDeletedTaxonomy(false);
    }

    return () => {};
  }, [shouldFilterDeletedTaxonomy, taxonomies, deletedParentAndChildId]);

  //Trigger filtering deleted taxonomy
  useEffect(() => {
    if (deletedParentAndChildId?.parent_id === taxonomy?.id) {
      setShouldFilterDeletedTaxonomy(true);
    }
  }, [taxonomy, deletedParentAndChildId]);

  //Fetch for new taxonomy children whenever a new one is created
  useEffect(() => {
    if (prevState && prevState?.actionMode !== actionMode && actionMode === ACTION_MODE.DEFAULT) {
      fetchTaxonomies();
    }
  }, [actionMode, prevState, fetchTaxonomies]);

  return (
    <>
      {!!(taxonomies && taxonomies?.length)
        ? taxonomies.map((taxonomy: GenericObject, index: number) => (
            <div
              key={taxonomy.id as number}
              style={{
                marginLeft: 30,
              }}>
              <TaxonomyTree
                index={index}
                handleChangeShouldExpandAll={handleChangeShouldExpandAll}
                shouldExpandAll={shouldExpandAll}
                deletedParentAndChildId={deletedParentAndChildId}
                actionMode={actionMode}
                taxonomy={taxonomy}
                handleShowInfo={handleShowInfo}
                selectedTaxonomy={selectedTaxonomy}
              />
            </div>
          ))
        : null}
    </>
  );
};
